import { useNavigate, useSearch } from '@tanstack/react-router';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PROJECT_PALETTE, type ProjectAdmin } from '@tt/shared';
import { ColorPicker } from '@/components/color-picker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/misc';
import { Segmented } from '@/components/ui/segmented';
import { Swatch } from '@/components/ui/swatch';
import { useAdminGroups, useAdminProjects, useProjectMutations } from '@/lib/admin-queries';
import { describeError, fieldErrorsOf } from '@/lib/errors';
import { formatHours } from '@/lib/format';
import { useFormat } from '@/lib/use-format';
import { AdminPage, ConfirmDialog, DataTable, MultiSelect, Pagination } from './admin-ui';

type StatusFilter = 'ALL' | 'ACTIVE' | 'ARCHIVED';

/** Projects: the catalog everyone books time to, and the colour each one is drawn with. */
export function AdminProjectsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/admin/projects' });
  const [editing, setEditing] = useState<ProjectAdmin | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ProjectAdmin | null>(null);

  const page = search.page ?? 1;
  const status: StatusFilter = (search.status as StatusFilter) ?? 'ACTIVE';
  const projects = useAdminProjects({
    q: search.q || undefined,
    status: status === 'ALL' ? undefined : status,
    page,
    pageSize: 25,
  });
  const groups = useAdminGroups({ pageSize: 200 });
  const mutations = useProjectMutations();

  const setSearch = (next: Record<string, unknown>) =>
    void navigate({
      to: '/admin/projects',
      search: { ...search, page: 1, ...next },
      replace: true,
    });

  const run = (promise: Promise<unknown>, message: string) =>
    promise.then(
      () => toast.success(message),
      (error: unknown) => toast.error(describeError(error, t)),
    );

  const rows = projects.data?.data ?? [];
  const groupOptions = (groups.data?.data ?? []).map((g) => ({ id: g.id, label: g.name }));

  return (
    <AdminPage
      title={t('admin.projects.title')}
      description={t('admin.projects.subtitle')}
      actions={
        <Button onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" aria-hidden="true" /> {t('admin.projects.new')}
        </Button>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          defaultValue={search.q ?? ''}
          onChange={(e) => setSearch({ q: e.target.value || undefined })}
          placeholder={t('admin.projects.search')}
          aria-label={t('admin.projects.search')}
          className="max-w-xs"
        />
        <Segmented
          value={status}
          onChange={(v) => setSearch({ status: v })}
          aria-label={t('admin.projects.status')}
          options={[
            { value: 'ACTIVE', label: t('status.ACTIVE') },
            { value: 'ARCHIVED', label: t('status.ARCHIVED') },
            { value: 'ALL', label: t('admin.all') },
          ]}
        />
      </div>

      <DataTable
        caption={t('admin.projects.title')}
        isPending={projects.isPending}
        error={projects.isError ? describeError(projects.error, t) : undefined}
        empty={t('admin.projects.none')}
        rows={rows}
        rowKey={(p) => p.id}
        footer={
          <Pagination
            page={page}
            totalPages={projects.data?.meta.totalPages ?? 1}
            total={projects.data?.meta.total ?? 0}
            onChange={(next) =>
              void navigate({ to: '/admin/projects', search: { ...search, page: next } })
            }
          />
        }
        columns={[
          {
            key: 'name',
            header: t('time.project'),
            cell: (p) => (
              <span className="inline-flex items-center gap-2">
                <Swatch color={p.color} className="h-2.5 w-2.5 shrink-0" />
                <span>
                  <span className="block font-medium">{p.name}</span>
                  {p.code || p.clientName ? (
                    <span className="block text-xs text-muted-foreground">
                      {[p.code, p.clientName].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                </span>
                {p.status === 'ARCHIVED' ? <Badge>{t('status.ARCHIVED')}</Badge> : null}
              </span>
            ),
          },
          {
            key: 'groups',
            header: t('admin.groups.title'),
            cell: (p) => (
              <GroupCell
                label={t('admin.projects.assignGroups', { name: p.name })}
                count={p.groupCount}
                options={groupOptions}
                onChange={(ids) =>
                  void run(
                    mutations.setGroups.mutateAsync({ id: p.id, ids }),
                    t('admin.projects.groupsSaved', { name: p.name }),
                  )
                }
              />
            ),
          },
          {
            key: 'hours',
            header: t('app.hours'),
            numeric: true,
            cell: (p) => formatHours(p.totalMinutes, fmt),
          },
          {
            key: 'color',
            header: t('admin.projects.color'),
            numeric: true,
            cell: (p) => (
              <ColorPicker
                value={p.color}
                label={p.name}
                onChange={(color) =>
                  void run(
                    mutations.update.mutateAsync({ id: p.id, color }),
                    t('admin.projects.colorSaved', { name: p.name }),
                  )
                }
              />
            ),
          },
          {
            key: 'actions',
            header: <span className="sr-only">{t('admin.actions')}</span>,
            numeric: true,
            cell: (p) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(p)}
                  aria-label={t('admin.projects.editName', { name: p.name })}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    void run(
                      mutations.setArchived.mutateAsync({
                        id: p.id,
                        archived: p.status === 'ACTIVE',
                      }),
                      p.status === 'ACTIVE'
                        ? t('admin.projects.archived', { name: p.name })
                        : t('admin.projects.unarchived', { name: p.name }),
                    )
                  }
                >
                  {p.status === 'ACTIVE' ? t('admin.archive') : t('admin.unarchive')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={p.entryCount > 0}
                  title={p.entryCount > 0 ? t('admin.projects.deleteBlocked') : undefined}
                  onClick={() => setDeleting(p)}
                >
                  {t('app.delete')}
                </Button>
              </div>
            ),
          },
        ]}
      />

      {editing ? (
        <ProjectDialog
          project={editing === 'new' ? null : editing}
          groupOptions={groupOptions}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('admin.projects.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('admin.projects.deleteBody')}
        confirmLabel={t('app.delete')}
        onConfirm={() => {
          if (!deleting) return;
          void run(
            mutations.remove.mutateAsync(deleting.id),
            t('admin.projects.deleted', { name: deleting.name }),
          );
        }}
      />
    </AdminPage>
  );
}

/**
 * The assignment cell starts from what the row reports rather than loading each project's
 * groups: the count is enough to describe the current state, and opening the picker is what
 * means "I am about to change this".
 */
function GroupCell({
  label,
  count,
  options,
  onChange,
}: {
  label: string;
  count: number;
  options: { id: string; label: string }[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[] | null>(null);
  return (
    <MultiSelect
      label={label}
      options={options}
      selected={selected ?? []}
      emptyLabel={t('admin.projects.groupCount', { count })}
      onChange={(ids) => {
        setSelected(ids);
        onChange(ids);
      }}
    />
  );
}

/** Create or edit: the same form either way, so the two cannot drift apart. */
function ProjectDialog({
  project,
  groupOptions,
  onClose,
}: {
  project: ProjectAdmin | null;
  groupOptions: { id: string; label: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mutations = useProjectMutations();
  const [form, setForm] = useState({
    name: project?.name ?? '',
    code: project?.code ?? '',
    clientName: project?.clientName ?? '',
    description: project?.description ?? '',
    color: project?.color ?? PROJECT_PALETTE[0]!.vivid,
    groupIds: [] as string[],
  });
  const [error, setError] = useState<unknown>(null);
  const fieldErrors = fieldErrorsOf(error);

  const submit = async () => {
    setError(null);
    const body = {
      name: form.name,
      code: form.code || null,
      clientName: form.clientName || null,
      description: form.description || null,
      color: form.color,
    };
    try {
      if (project) await mutations.update.mutateAsync({ id: project.id, ...body });
      else await mutations.create.mutateAsync({ ...body, groupIds: form.groupIds });
      toast.success(
        project
          ? t('admin.projects.saved', { name: form.name })
          : t('admin.projects.created', { name: form.name }),
      );
      onClose();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {project
              ? t('admin.projects.editName', { name: project.name })
              : t('admin.projects.new')}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label={t('time.project')} required error={fieldErrors.name}>
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.projects.code')} error={fieldErrors.code}>
              {(props) => (
                <Input
                  {...props}
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                />
              )}
            </Field>
            <Field label={t('admin.projects.client')} error={fieldErrors.clientName}>
              {(props) => (
                <Input
                  {...props}
                  value={form.clientName}
                  onChange={(e) => setForm((f) => ({ ...f, clientName: e.target.value }))}
                />
              )}
            </Field>
          </div>
          <Field label={t('admin.projects.description')} error={fieldErrors.description}>
            {(props) => (
              <Input
                {...props}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            )}
          </Field>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('admin.projects.color')}>
              {() => (
                <ColorPicker
                  value={form.color}
                  label={form.name || t('time.project')}
                  onChange={(color) => setForm((f) => ({ ...f, color }))}
                />
              )}
            </Field>
            {project ? null : (
              <div className="min-w-48 flex-1">
                <Field label={t('admin.groups.title')}>
                  {() => (
                    <MultiSelect
                      label={t('admin.groups.title')}
                      options={groupOptions}
                      selected={form.groupIds}
                      emptyLabel={t('admin.projects.noGroups')}
                      onChange={(groupIds) => setForm((f) => ({ ...f, groupIds }))}
                    />
                  )}
                </Field>
              </div>
            )}
          </div>
          {error && Object.keys(fieldErrors).length === 0 ? (
            <p className="text-sm text-destructive" role="alert">
              {describeError(error, t)}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('app.cancel')}
            </Button>
            <Button type="submit" disabled={!form.name.trim()}>
              {t('app.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
